// Pure helpers that turn a normalised "core" order (produced by the per-platform
// importers or the manual-entry form) into the full Firestore document shape
// described in ORDER_SCHEMA.md. No Firestore access here — see ordersService.js.

import {
  LEGACY_CHANNEL,
  STATUS,
  isActiveStatus,
  rollUpStatus,
  effectiveStatus,
  isEmbroideryProduct,
} from '../constants';
import { countryToCode, isEUCountry } from './countries';
import { calculatePostByDate, earliestDate } from './dates';
import { extractColour } from './colours';

const lower = (v) => (v ? String(v).trim().toLowerCase() : '');
const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

/** Firestore document id: `${source}_${platformOrderId}` (idempotent re-import). */
export function buildDocId(source, platformOrderId) {
  return `${source}_${platformOrderId}`;
}

/**
 * Complete a core order: fill derived fields (totals, lowercased search keys,
 * country code, EU/IOSS, post-by dates, leather/active flags, sortKey).
 * Timestamps (meta.importedAt/updatedAt) are stamped by the service at write time.
 */
export function finalizeOrder(core) {
  const source = core.source;
  const legacyChannel = LEGACY_CHANNEL[source] || 'Direct';
  const platformOrderId = String(core.platformOrderId ?? '');
  const docId = core.docId || buildDocId(source, platformOrderId);

  const lineItems = (core.lineItems || []).map((li, i) => ({
    lineId: li.lineId || `li_${i + 1}`,
    product: li.product || '',
    productRaw: li.productRaw || '',
    variation: li.variation || '',
    buyerNotes: li.buyerNotes || '',
    quantity: num(li.quantity) || 1,
    unitPrice: num(li.unitPrice),
    tax: li.tax ?? null,
    duty: li.duty ?? null,
    sku: li.sku || '',
    isLeather: !!li.isLeather,
    isEmbroidery: li.isEmbroidery ?? isEmbroideryProduct(li.product),
    colour: li.colour || extractColour(`${li.variation || ''} ${li.product || ''}`), // derived from variation
    // per-line status; embroidery lines start "Pending", felt lines "Uncut"
    status: li.status || (li.isEmbroidery ?? isEmbroideryProduct(li.product) ? STATUS.PENDING : STATUS.UNCUT),
    sortOrder: li.sortOrder ?? i,
  }));

  const itemsTotal = lineItems.reduce((sum, li) => sum + li.unitPrice * li.quantity, 0);
  const postage = num(core.money?.postage);
  const discount = num(core.money?.discount);
  const refund = num(core.money?.refund);
  const total = itemsTotal + postage + refund - discount;

  const ordered = core.dates?.ordered || null;
  const postByPlatform = core.dates?.postByPlatform || null;
  const postByTarget = ordered ? calculatePostByDate(ordered) : null;
  const postByEffective = earliestDate(postByTarget, postByPlatform);

  const country = core.shipping?.country || '';
  const countryCode = core.shipping?.countryCode || countryToCode(country);

  // Two-tier status: production rolls up from line items; manual override wins.
  const manualStatus = core.workflow?.manualStatus || null;
  const productionStatus = rollUpStatus(lineItems);
  const status = effectiveStatus(manualStatus, lineItems);
  const isLeather = lineItems.some((li) => li.isLeather) || !!core.workflow?.isLeather;

  return {
    docId,

    source,
    legacyChannel,
    platformOrderId,
    platformOrderId2: String(core.platformOrderId2 ?? ''),
    compositeKey: core.compositeKey || `${legacyChannel}|${platformOrderId}`,

    buyer: {
      userId: core.buyer?.userId || '',
      name: core.buyer?.name || '',
      nameLower: lower(core.buyer?.name),
      email: core.buyer?.email || '',
      emailLower: lower(core.buyer?.email),
    },

    shipping: {
      name: core.shipping?.name || '',
      phone: core.shipping?.phone || '',
      address1: core.shipping?.address1 || '',
      address1Lower: lower(core.shipping?.address1),
      address2: core.shipping?.address2 || '',
      townCity: core.shipping?.townCity || '',
      county: core.shipping?.county || '',
      postcode: core.shipping?.postcode || '',
      postcodeLower: lower(core.shipping?.postcode).replace(/\s+/g, ''),
      country,
      countryCode,
      service: core.shipping?.service || '',
      isEU: country ? isEUCountry(country) : false,
      ioss: !!core.shipping?.ioss,
    },

    lineItems,

    money: {
      currency: core.money?.currency || 'GBP',
      itemsTotal,
      postage,
      discount,
      refund,
      tax: core.money?.tax ?? null,
      duties: core.money?.duties ?? null,
      total,
    },

    dates: {
      ordered,
      paid: core.dates?.paid || null,
      postByTarget,
      postByPlatform,
      postByEffective,
    },

    workflow: {
      status, // effective (manual override ?? production roll-up) — for tabs/colour/queries
      manualStatus, // null = auto/derived; otherwise Kate's order-level override
      productionStatus, // derived roll-up of line-item statuses
      isActive: isActiveStatus(status),
      posted: !!core.workflow?.posted,
      trackingNumber: core.workflow?.trackingNumber || '',
      egNotes: core.workflow?.egNotes || '',
      isLeather,
      ftoc: !!core.workflow?.ftoc,
      sortKey: core.workflow?.sortKey ?? (ordered ? ordered.getTime() : 0),
    },

    repeatCustomer: {
      verdict: core.repeatCustomer?.verdict || 'no',
      basis: core.repeatCustomer?.basis || [],
      confirmedByUser: core.repeatCustomer?.confirmedByUser ?? null,
    },

    meta: {
      manualEntry: !!core.meta?.manualEntry,
      importBatchId: core.meta?.importBatchId || '',
      schemaVersion: 1,
    },

    raw: core.raw || {},
  };
}
